"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuthStore } from "../store/authStore"
import { useChatStore } from "../store/chatStore"
import ContactList from "../components/ContactList"
import ChatWindow from "../components/ChatWindow"
import AddContactModal from "../components/AddContactModal"

export const ChatPage: React.FC = () => {
  const navigate = useNavigate()
  const { token, username, logout } = useAuthStore()
  const { activeChat, setActiveChat } = useChatStore()
  const [showAddContactModal, setShowAddContactModal] = useState(false)
  const [showSidebar, setShowSidebar] = useState(true)

  useEffect(() => {
    if (!token) {
      navigate("/login")
    }
  }, [token, navigate])

  const handleLogout = () => {
    logout()
    navigate("/login")
  }

  return (
    <div className="h-screen flex flex-col bg-slate-900">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Mobile menu toggle */}
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            className="lg:hidden text-white p-2 hover:bg-slate-700 rounded"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-white">MeeChat</h1>
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          <span className="text-slate-300 text-sm sm:text-base hidden sm:inline">Welcome, {username}</span>
          <span className="text-slate-300 text-sm sm:hidden">{username}</span>
          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base rounded cursor-pointer"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex gap-2 sm:gap-4 p-2 sm:p-4 overflow-hidden relative">
        {/* Contacts Sidebar */}
        <div
          className={`
          ${showSidebar ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
          fixed lg:relative
          inset-y-0 left-0
          z-40
          w-64 sm:w-72 lg:w-64 xl:w-80
          bg-slate-800 rounded-none lg:rounded-lg border-r lg:border border-slate-700 flex flex-col
          transition-transform duration-300 ease-in-out
          mt-[57px] sm:mt-[65px] lg:mt-0
        `}
        >
          <div className="p-3 sm:p-4 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-base sm:text-lg font-semibold text-white">Contacts</h2>
            <button
              onClick={() => setShowAddContactModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs sm:text-sm cursor-pointer"
            >
              + Add
            </button>
          </div>
          <ContactList onSelectContact={() => setShowSidebar(false)} />
        </div>

        {/* Overlay for mobile */}
        {showSidebar && (
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-30 mt-[57px] sm:mt-[65px]"
            onClick={() => setShowSidebar(false)}
          />
        )}

        {/* Chat Window */}
        <div className="flex-1 min-w-0">
          {activeChat ? (
            <ChatWindow
              onBack={() => {
                setActiveChat(null)
                setShowSidebar(true)
              }}
            />
          ) : (
            <div className="h-full bg-slate-800 rounded-lg border border-slate-700 flex items-center justify-center">
              <p className="text-slate-400 text-sm sm:text-base px-4 text-center">Select a contact to start chatting</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Contact Modal */}
      {showAddContactModal && (
        <AddContactModal
          onClose={() => setShowAddContactModal(false)}
          onContactAdded={() => setShowAddContactModal(false)}
        />
      )}
    </div>
  )
}
